import React, { useMemo, useState } from 'react'
import { format, parseISO } from 'date-fns'
import {
  IAnomaliesV2Network,
  IAnomaliesV2SeriesPoint,
} from '../../shared/types'

// Sits directly under the Primary metric chart. Each row is one affiliate
// network from the cross-window summary; each column is one day from the
// loaded series (already filtered to the active device upstream). A filled
// cell means the network was active for the advertiser on that day. The eye
// lines up "Awin went dark" against "CR dropped" without a click.
//
// We intentionally don't try to perfectly align with the chart's plot area
// pixel-for-pixel — that would require knowing the YAxis width at render
// time. We mirror its margins via padding so the alignment is close enough
// to read as the same timeline.

const CELL_MIN_WIDTH = 2 // px, before the row grows to fill its track

// A stable palette to differentiate networks at a glance. Picked so all
// colours hold up against the page background and the chart's blue/green.
const PALETTE = [
  '#4F9EEC',
  '#5DC97E',
  '#A675F5',
  '#EBB617',
  '#E55353',
  '#3FB8B5',
  '#F08AB5',
  '#7A8BFF',
]

const xTickFormat = (s: string) => {
  try {
    return format(parseISO(s), 'MMM d')
  } catch {
    return s
  }
}

export const NetworkLane = (props: {
  series: IAnomaliesV2SeriesPoint[]
  networks: IAnomaliesV2Network[]
  selectedDate?: string
}) => {
  const { series, networks, selectedDate } = props

  // Empty state — either the backend hasn't sent network data yet, or this
  // advertiser truly has none in the window.
  const hasAnyNetwork =
    networks.length > 0 && series.some((p) => (p.networks ?? []).length > 0)

  // Pre-index network presence: for each (network uuid, eventDate) we want
  // O(1) lookup when rendering the grid.
  const presence = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const point of series) {
      for (const n of point.networks ?? []) {
        const set = map.get(n.uuid) ?? new Set<string>()
        set.add(point.eventDate)
        map.set(n.uuid, set)
      }
    }
    return map
  }, [series])

  // Stable colour per network — first-seen-in-summary order so colour
  // assignment is deterministic across renders.
  const colourFor = useMemo(() => {
    const m = new Map<string, string>()
    networks.forEach((n, i) => m.set(n.uuid, PALETTE[i % PALETTE.length]))
    return m
  }, [networks])

  if (!hasAnyNetwork) return null

  // Selected-date "today" guide so the operator can see exactly where the
  // page's selected date falls in the lane.
  const selectedIdx = selectedDate
    ? series.findIndex((p) => p.eventDate === selectedDate)
    : -1

  // Hover state: the date the user is pointing at and (optionally) which
  // network row they're hovering. We show this in the header so the user can
  // read both at once without waiting for the OS tooltip.
  const [hover, setHover] = useState<
    { date: string; networkName: string; active: boolean } | null
  >(null)

  // The track is rendered inside a grid row where the network label sits in
  // a fixed-width left column. The bottom axis labels need to align with the
  // track itself, not the card's left edge — so they live INSIDE the same
  // grid layout and share the column structure.

  return (
    <div className="card anomalies-network-lane">
      <div className="card-body">
        <div className="row row-space-between row-center m-b-2" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="text-dark text-bolder">Networks active</div>
          <div className="text-light text-small">
            {hover ? (
              <>
                <b>{hover.networkName}</b> · {xTickFormat(hover.date)} ·{' '}
                {hover.active ? 'active' : 'inactive'}
              </>
            ) : (
              <>One row per network · cell filled when the network was active on that day</>
            )}
          </div>
        </div>

        <div className="anomalies-network-lane-grid">
          {networks.map((network) => {
            const dates = presence.get(network.uuid) ?? new Set<string>()
            const colour = colourFor.get(network.uuid) || '#828B9E'
            return (
              <div key={network.uuid} className="anomalies-network-lane-row">
                <div className="anomalies-network-lane-label">
                  <span
                    className="anomalies-network-lane-swatch"
                    style={{ background: colour }}
                  />
                  <span className="text-dark text-small">{network.name}</span>
                  <span className="text-light text-small">
                    {network.activeDays}d
                  </span>
                </div>
                <div
                  className="anomalies-network-lane-track"
                  onMouseLeave={() => setHover(null)}
                >
                  {series.map((point, i) => {
                    const active = dates.has(point.eventDate)
                    const isSelected = i === selectedIdx
                    return (
                      <div
                        key={point.eventDate}
                        className="anomalies-network-lane-cell"
                        title={`${network.name} · ${xTickFormat(point.eventDate)}${
                          active ? ' · active' : ' · inactive'
                        }`}
                        onMouseEnter={() =>
                          setHover({
                            date: point.eventDate,
                            networkName: network.name,
                            active,
                          })
                        }
                        style={{
                          minWidth: CELL_MIN_WIDTH,
                          background: active ? colour : 'transparent',
                          outline: isSelected
                            ? '1px solid rgba(229, 83, 83, 0.55)'
                            : undefined,
                        }}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}

          {/* Bottom axis row sits in the same grid columns as the tracks, so
              its left/right labels line up exactly with the track edges. */}
          <div className="anomalies-network-lane-row anomalies-network-lane-axis-row">
            <div />
            <div className="anomalies-network-lane-axis">
              <span className="text-light text-small">
                {series.length > 0 ? xTickFormat(series[0].eventDate) : ''}
              </span>
              <span className="text-light text-small">
                {series.length > 0
                  ? xTickFormat(series[series.length - 1].eventDate)
                  : ''}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
